import Logger from "../lib/logger";
import DecorativeAnimation from '../lib/decorative-animation.js';

class ThemeFooter {
	NAME = 'ThemeFooter';

	/**
	 * Class static initialization block
	 */
	init() {
		Logger.info(`${this.NAME} > init`);

		this.footer_section = {
			bunny_animation: document.querySelector(`[data-theme-footer-bunny-animation]`),
			footer: document.querySelector("[data-theme-footer]"),
			footerPlaceholder: document.querySelector(`[data-theme-footer-placeholder]`),
			inner: document.querySelectorAll(".theme-footer__block-inner"),
			pluses_minuses: document.querySelectorAll(".theme-footer__block-btn-icon"),
			services: document.querySelector(`.theme-footer-services`),
			support_pane: document.querySelector(`[data-theme-footer-support-pane]`),
			support_pane_toggles: document.querySelectorAll(`[data-theme-footer-support-pane-toggle]`),
			footer_form: document.querySelector(`.theme-footer__newsletter-input`),
		};

		if (!this.footer_section.footer) return;

		this.mobile_nav = {
			nav: document.getElementById("theme-mobile_nav"),
			setWindowInnerHeightVar: () => {
				// interior height of the window in pixels
				document.documentElement.style.setProperty(
					"--window-inner-height",
					`${window.innerHeight}px`
				);

				const footerHeight = this.footer_section.footer.offsetHeight;
				this.footer_section.footerPlaceholder.style.height = `${footerHeight}px`;
			}
		};

		this.addHandlers();
		this.initSupportPaneAutoOpen();
		this.initBunnyAnimation();
		this.initFooterForm();
	}

	initSupportPaneAutoOpen() {
		const delaySeconds = Number.parseInt(
			this.footer_section.services?.dataset.themeFooterSupportAutoOpenDelay,
			10
		);

		if (!Number.isFinite(delaySeconds) || delaySeconds <= 0) return;

		let timeoutId = window.setTimeout(() => {
			timeoutId = null;
			this.footer_section.support_pane.classList.add(`theme-footer-support-pane--active`);
		}, delaySeconds * 1000);

		this.footer_section.support_pane_toggles.forEach(toggle => {
			toggle.addEventListener(`click`, () => {
				if (!timeoutId) return;
				window.clearTimeout(timeoutId);
				timeoutId = null;
			}, { once: true });
		});
	}

	/**
	 * addHandlers --- Add event handlers for view
	 */
	addHandlers() {
		Logger.info(`${this.NAME} > addHandlers`);
		window.addEventListener("resize", this.mobile_nav.setWindowInnerHeightVar);
		this.mobile_nav.setWindowInnerHeightVar();
		this.footer_section.footer.addEventListener("click", this.onFooter.bind(this), true);

		// Support pane
		this.footer_section.support_pane_toggles.forEach(toggle => {
			toggle.addEventListener(`click`, e => {
				e.preventDefault();
				this.footer_section.support_pane.classList.toggle(`theme-footer-support-pane--active`);
			});
		});
	}

	/**
	 * initBunnyAnimation - Initialize the bunny animation
	 */
	initBunnyAnimation() {
		Logger.info(`${this.NAME} > initBunnyAnimation`);
		const container = this.footer_section.bunny_animation;
		if (!container || !window.IntersectionObserver) return;
		const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
		const bunnyAnimation = new DecorativeAnimation(container, {
			loop: true,
			preserveFallback: true,
		});
		const placeholder = this.footer_section.footerPlaceholder;
		let bunnyVisible = false;
		let revealVisible = false;
		const update = () => {
			// A fixed footer intersects the viewport even while the page covers it.
			// Its normal-flow placeholder enters view only as the footer is revealed.
			const fixed = getComputedStyle(this.footer_section.footer).position === 'fixed';
			const visible = bunnyVisible && (!fixed || revealVisible);
			if (motion.matches) bunnyAnimation.destroy();
			else if (visible && !document.hidden) void bunnyAnimation.play();
			else bunnyAnimation.pause();
		};
		const observer = new IntersectionObserver(entries => {
			for (const entry of entries) {
				if (entry.target === container) bunnyVisible = entry.isIntersecting;
				if (entry.target === placeholder) revealVisible = entry.isIntersecting;
			}
			update();
		});
		const onPageHide = () => bunnyAnimation.destroy();
		const onUnload = event => {
			if (!event.target.contains(container)) return;
			observer.disconnect();
			bunnyAnimation.destroy();
			motion.removeEventListener('change', update);
			document.removeEventListener('visibilitychange', update);
			document.removeEventListener('shopify:section:unload', onUnload);
			window.removeEventListener('pagehide', onPageHide);
			window.removeEventListener('pageshow', update);
		};
		observer.observe(container);
		if (placeholder) observer.observe(placeholder);
		motion.addEventListener('change', update);
		document.addEventListener('visibilitychange', update);
		document.addEventListener('shopify:section:unload', onUnload);
		window.addEventListener('pagehide', onPageHide);
		window.addEventListener('pageshow', update);
	}

	/**
	 * initFooterForm - Initialize the subscribe form in the footer
	 */
	initFooterForm() {
		Logger.info(`${this.NAME} > initFooterForm`);
		if (window.themeTrackingSuppressed || !window.KlaviyoSubscribe) return;

		window.KlaviyoSubscribe.attachToForms(this.footer_section.footer_form, {
			hide_form_on_success: true,
			extra_properties: {
				$source: '$embed',
				$method_type: "Klaviyo Form",
				$method_id: 'embed',
				$consent_version: 'Embed default text'
			},
			success: function ($form) {
				setTimeout(function(){
					$('.nl-popup').css({'opacity': '0', 'pointer-events': 'none' });
				}, 3000);
				console.log($form);
				window.location.href = '/pages/thankyou';
			},
			error: function(error) {
				console.log(error);
			}
		});
	}

	/**
	 * onFooter
	 * @param {*} evt
	 */
	onFooter(evt) {
		const target = evt.target;

		if (target && target.nodeName === "BUTTON") {
			let classes = target.className.split(" ");
			if (classes) {
				// Search for class and react on match
				for (let x = 0; x < classes.length; x++) {
					switch (classes[x]) {
						/**
						 * Open clam-shell menu (and close open menus)
						 */
						case "theme-footer__block-btn":
							const siblings = this.getSiblings(target);

							// Close all clam-shells
							this.footer_section.inner.forEach((element) => {
								if (element.nodeName === "DIV")
									element.removeAttribute("aria-expanded");
							});

							// Open all plus_minuses
							this.footer_section.pluses_minuses.forEach((element) => {
								if (element.nodeName === "DIV")
									element.setAttribute("aria-expanded", "true");
							});

							siblings.forEach((element) => {
								if (element.nodeName === "DIV") {
									element.setAttribute("aria-expanded", "true");
									target.children[1].setAttribute("aria-expanded", "false"); // pluses_minuses icon toggle to minus sign of selected button

									// Below will scroll the page so that the button in question is aligned to the top of the page
									// but it will not work correctly in this setting
									// location.href = window.location.href + `#${target.id}`;
								}
							});
							break;
						default:
							break;
					}
				}
			}
		}
	}

	/**
	 * getSiblings --- Get element’s siblings
	 * @param elem
	 * @returns {*[]}
	 */
	getSiblings(elem) {
		// Setup siblings array the get the first sibling
		let siblings = [];
		let sibling = elem.parentNode.firstChild;
		// Loop through each sibling and push into the array
		while (sibling) {
			if (sibling.nodeType === 1 && sibling !== elem) {
				siblings.push(sibling);
			}
			sibling = sibling.nextSibling;
		}
		return siblings;
	};
}

export default ThemeFooter;
